import { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { API } from "@/App";
import { DiscardConfirm } from "@/components/DiscardConfirm";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { formatDate } from "@/lib/dateTime";
import { useApiCrud } from "@/hooks/useApiCrud";
import { useColumnManager } from "@/hooks/useColumnManager";
import { ColumnToolbar } from "@/components/ColumnToolbar";
import { ColumnModals } from "@/components/ColumnModals";
import { CustomerNameFields } from "@/components/CustomerNameFields";
import { PageHeader } from "@/components/PageHeader";
import {
  Shield,
  Plus,
  Filter,
  Search,
  RefreshCw,
  Flag,
  X,
  Save,
  Upload,
  FileText,
  Trash2,
  Pencil,
  Download,
  AlertTriangle,
} from "lucide-react";

const COVERAGE_TYPES = [
  "1Y OD + 3Y TP",
  "3Y OD + 3Y TP",
  "SAOD",
  "SATP",
  "Comprehensive",
];

const STATUS_OPTIONS = ["ACTIVE", "EXPIRED", "LOST"];

const DEFAULT_COLUMNS = [
  { id: "customer", label: "Customer" },
  { id: "vehicle", label: "Vehicle" },
  { id: "insurer", label: "Insurer" },
  { id: "coverage", label: "Coverage" },
  { id: "expires", label: "Expires" },
  { id: "status", label: "Status" },
  { id: "flag", label: "Flag" },
  { id: "actions", label: "" },
];
const CUSTOMIZABLE_IDS = ["customer", "vehicle", "insurer", "coverage", "expires", "status", "flag"];

const EMPTY_FORM = {
  vin: "",
  vehicle_reg_no: "",
  model: "",
  salutation: "Mr",
  first_name: "",
  last_name: "",
  company_name: "",
  customer_phone: "",
  alternate_phone: "",
  insurer: "",
  policy_number: "",
  coverage_type: "1Y OD + 3Y TP",
  od_valid_from: "",
  od_valid_to: "",
  policy_premium: "",
  source: "",
};

function StatusBadge({ status }) {
  const cls =
    status === "ACTIVE"
      ? "bg-green-100 text-green-800 border-green-200"
      : status === "EXPIRED"
      ? "bg-amber-100 text-amber-800 border-amber-200"
      : "bg-red-100 text-red-800 border-red-200";
  return (
    <Badge
      variant="outline"
      className={`rounded-sm text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 ${cls}`}
      data-testid={`status-${status}`}
    >
      {status}
    </Badge>
  );
}

export default function InsurancePolicies() {
  const policiesCrud = useApiCrud("/insurance/policies");
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({});

  // Filters / search
  const [search, setSearch] = useState("");
  const [filterOpen, setFilterOpen] = useState(false);
  const [filters, setFilters] = useState({ status: "", insurer: "", source: "", flagged: "" });

  // Add / edit modal
  const [formOpen, setFormOpen] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [editingPolicyId, setEditingPolicyId] = useState(null);
  const [formDiscardOpen, setFormDiscardOpen] = useState(false);
  const formSnapshotRef = useRef(null);
  const [policyFile, setPolicyFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deletingPolicy, setDeletingPolicy] = useState(false);

  // Detail drawer
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerPolicy, setDrawerPolicy] = useState(null);
  const [drawerHistory, setDrawerHistory] = useState([]);

  const columnManager = useColumnManager("insurance-policies", DEFAULT_COLUMNS, CUSTOMIZABLE_IDS);
  const visibleCols = columnManager.visibleCols;

  const updateField = (field, value) =>
    setFormData((prev) => ({ ...prev, [field]: value }));

  const fetchPolicies = async () => {
    setLoading(true);
    const params = {};
    if (filters.status) params.status = filters.status;
    if (filters.insurer) params.insurer = filters.insurer;
    if (filters.source) params.source = filters.source;
    if (filters.flagged) params.flagged = filters.flagged;
    if (search) params.search = search;
    try {
      const data = await policiesCrud.fetchData(params);
      setPolicies(data || []);
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/settings`, { withCredentials: true });
      setSettings(res.data || {});
    } catch {}
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  useEffect(() => {
    fetchPolicies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounced search
  useEffect(() => {
    const t = setTimeout(fetchPolicies, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const applyFilters = () => {
    setFilterOpen(false);
    fetchPolicies();
  };

  const clearFilters = () => {
    setFilters({ status: "", insurer: "", source: "", flagged: "" });
  };

  const openAdd = () => {
    setFormData(EMPTY_FORM);
    setEditingPolicyId(null);
    setPolicyFile(null);
    setFormOpen(true);
  };

  // Snapshot the form when the modal opens — dirty = form changed or file attached.
  useEffect(() => {
    if (formOpen) formSnapshotRef.current = JSON.stringify(formData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formOpen]);

  const openEdit = async (policy) => {
    setEditingPolicyId(policy.policy_id);
    setFormData({
      vin: policy.vin || "",
      vehicle_reg_no: policy.vehicle_reg_no || "",
      model: policy.model || "",
      salutation: policy.salutation || "Mr",
      first_name: policy.first_name || "",
      last_name: policy.last_name || "",
      company_name: policy.company_name || "",
      customer_phone: policy.customer_phone || "",
      alternate_phone: policy.alternate_phone || "",
      insurer: policy.insurer || "",
      policy_number: policy.policy_number || "",
      coverage_type: policy.coverage_type || "1Y OD + 3Y TP",
      od_valid_from: policy.od_valid_from || "",
      od_valid_to: policy.od_valid_to || "",
      policy_premium: policy.policy_premium ?? "",
      source: policy.source || "",
    });
    setPolicyFile(null);
    setFormOpen(true);
  };

  // VIN auto-prefill — fires when VIN field reaches a typical chassis length
  const tryPrefillFromVin = async (vinValue) => {
    const v = (vinValue || "").trim().toUpperCase();
    if (v.length < 11) return;
    if (editingPolicyId) return; // don't auto-overwrite during edit
    try {
      const res = await axios.get(
        `${API}/insurance/lookup-vin?vin=${encodeURIComponent(v)}`,
        { withCredentials: true }
      );
      if (!res.data) return;
      const d = res.data;
      setFormData((prev) => ({
        ...prev,
        vehicle_reg_no: prev.vehicle_reg_no || d.vehicle_reg_no || "",
        model: prev.model || d.model || "",
        salutation: prev.salutation === "Mr" && !prev.first_name && !prev.company_name
          ? (d.salutation || "Mr")
          : prev.salutation,
        first_name: prev.first_name || d.first_name || "",
        last_name: prev.last_name || d.last_name || "",
        company_name: prev.company_name || d.company_name || "",
        customer_phone: prev.customer_phone || d.customer_phone || "",
      }));
      toast.success("Prefilled from vehicle record");
    } catch {}
  };

  // Frontend mirror of backend reconfirm check — gives instant warning in form
  const reconfirmWarning = useMemo(() => {
    const { coverage_type, od_valid_from, od_valid_to } = formData;
    if (!od_valid_from || !od_valid_to) return null;
    const from = new Date(od_valid_from);
    const to = new Date(od_valid_to);
    if (isNaN(from) || isNaN(to)) return null;
    if (to <= from) return "End date is on or before start date";
    if (coverage_type === "SATP") return null;
    const years = coverage_type === "3Y OD + 3Y TP" ? 3 : 1;
    const expected = new Date(from);
    expected.setFullYear(expected.getFullYear() + years);
    const diffDays = Math.abs((to - expected) / (1000 * 60 * 60 * 24));
    if (diffDays > 1) return `Coverage ${coverage_type} usually spans ${years} year(s); dates differ by ${Math.round(diffDays)} day(s)`;
    return null;
  }, [formData.coverage_type, formData.od_valid_from, formData.od_valid_to]);

  const handleSave = async () => {
    // Local validation
    if (!formData.vin?.trim()) return toast.error("VIN is required");
    if (!formData.insurer) return toast.error("Insurer is required");
    if (!formData.policy_number?.trim()) return toast.error("Policy number is required");
    if (!formData.coverage_type) return toast.error("Coverage type is required");
    if (!formData.od_valid_from) return toast.error("OD Valid From is required");
    if (!formData.od_valid_to) return toast.error("OD Valid To is required");
    if (!formData.customer_phone?.trim()) return toast.error("Customer phone is required");
    if (!formData.source) return toast.error("Source is required");
    if (formData.salutation === "M/S") {
      if (!formData.company_name?.trim()) return toast.error("Company name is required");
    } else {
      if (!formData.first_name?.trim() || !formData.last_name?.trim()) {
        return toast.error("First and last name are required");
      }
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        vehicle_reg_no: formData.vehicle_reg_no?.trim() || "PENDING",
        policy_premium: formData.policy_premium === "" ? null : Number(formData.policy_premium),
      };

      // Attach base64 file if user picked one
      if (policyFile) {
        const b64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result.split(",")[1]);
          reader.onerror = reject;
          reader.readAsDataURL(policyFile);
        });
        payload.policy_file_data = b64;
        payload.policy_file_name = policyFile.name;
        payload.policy_file_type = policyFile.type;
      }

      if (editingPolicyId) {
        await axios.put(
          `${API}/insurance/policies/${editingPolicyId}`,
          payload,
          { withCredentials: true }
        );
        toast.success("Policy updated");
      } else {
        await axios.post(`${API}/insurance/policies`, payload, { withCredentials: true });
        toast.success("Policy created");
      }
      setFormOpen(false);
      fetchPolicies();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save policy");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (policyId) => {
    if (deletingPolicy) return;
    if (!window.confirm("Delete this policy? This cannot be undone.")) return;
    setDeletingPolicy(true);
    try {
      await axios.delete(`${API}/insurance/policies/${policyId}`, { withCredentials: true });
      toast.success("Policy deleted");
      if (drawerPolicy?.policy_id === policyId) setDrawerOpen(false);
      fetchPolicies();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete");
    } finally {
      setDeletingPolicy(false);
    }
  };

  const openDrawer = async (policyId) => {
    try {
      const res = await axios.get(`${API}/insurance/policies/${policyId}`, { withCredentials: true });
      setDrawerPolicy(res.data.policy);
      setDrawerHistory(res.data.history || []);
      setDrawerOpen(true);
    } catch {
      toast.error("Failed to load policy details");
    }
  };

  const downloadFile = (policyId) => {
    window.open(`${API}/insurance/policies/${policyId}/file`, "_blank");
  };

  // ---------- RENDER ----------

  const renderCell = (col, p) => {
    switch (col.id) {
      case "customer":
        return (
          <div>
            <p className="font-medium text-sm">{p.customer_name}</p>
            <p className="text-xs text-gray-500">{p.customer_phone}</p>
          </div>
        );
      case "vehicle":
        return (
          <div>
            <p className="font-medium text-sm">{p.model || "-"}</p>
            <p className="text-xs text-gray-500">{p.vehicle_reg_no}</p>
          </div>
        );
      case "insurer":
        return <span className="text-sm">{p.insurer}</span>;
      case "coverage":
        return <span className="text-xs">{p.coverage_type}</span>;
      case "expires":
        return <span className="text-sm">{formatDate(p.od_valid_to)}</span>;
      case "status":
        return <StatusBadge status={p.status} />;
      case "flag":
        return p.reconfirm_flag ? (
          <Flag className="w-4 h-4 text-red-600" strokeWidth={2} data-testid="reconfirm-flag" />
        ) : null;
      case "actions":
        return (
          <div className="flex items-center gap-1 justify-end">
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={(e) => {
                e.stopPropagation();
                openEdit(p);
              }}
              data-testid={`edit-${p.policy_id}`}
            >
              <Pencil className="w-3.5 h-3.5" strokeWidth={1.5} />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-red-600"
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(p.policy_id);
              }}
              data-testid={`delete-${p.policy_id}`}
            >
              <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
            </Button>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6" data-testid="insurance-policies-page">
      {/* Standard Header */}
      <PageHeader
        icon={Shield}
        title="Insurance"
        subtitle="All Policies"
        onFilter={() => setFilterOpen(true)}
        filterTestId="insurance-filter-btn"
        onRefresh={fetchPolicies}
        refreshTestId="insurance-refresh-btn"
        primaryAction={{
          label: "Add Policy",
          onClick: openAdd,
          icon: Plus,
          testId: "insurance-add-policy-btn",
        }}
        search={{
          value: search,
          onChange: setSearch,
          placeholder: "Search customer / VIN / Reg / Policy #",
          testId: "insurance-search",
        }}
        columnControls={{
          onCustomize: () => columnManager.openCustomize(),
          onRearrange: () => columnManager.openRearrange(),
        }}
      />

      {/* Table */}
      <Card className="border border-gray-200 rounded-sm shadow-none overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="w-8 h-8 border-2 border-black border-t-transparent rounded-full animate-spin" />
          </div>
        ) : policies.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-48 text-gray-400">
            <Shield className="w-8 h-8 mb-2" strokeWidth={1.5} />
            <p className="text-sm">No policies match the current filters.</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                {visibleCols.map((col) => (
                  <TableHead
                    key={col.id}
                    className="text-[10px] font-bold uppercase tracking-widest text-gray-500"
                  >
                    {col.label}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {policies.map((p) => (
                <TableRow
                  key={p.policy_id}
                  className="cursor-pointer hover:bg-gray-50"
                  onClick={() => openDrawer(p.policy_id)}
                  data-testid={`policy-row-${p.policy_id}`}
                >
                  {visibleCols.map((col) => (
                    <TableCell key={col.id}>{renderCell(col, p)}</TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      {/* Filter Modal */}
      <Dialog open={filterOpen} onOpenChange={setFilterOpen}>
        <DialogContent className="rounded-sm max-w-xl" data-testid="insurance-filter-modal">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold text-xl tracking-tight uppercase">
              Filter Policies
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 pt-4">
            <div>
              <Label className="form-label">Status</Label>
              <Select
                value={filters.status || "all"}
                onValueChange={(v) => setFilters((f) => ({ ...f, status: v === "all" ? "" : v }))}
              >
                <SelectTrigger className="rounded-sm" data-testid="filter-status">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {STATUS_OPTIONS.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="form-label">Insurer</Label>
              <Select
                value={filters.insurer || "all"}
                onValueChange={(v) => setFilters((f) => ({ ...f, insurer: v === "all" ? "" : v }))}
              >
                <SelectTrigger className="rounded-sm" data-testid="filter-insurer">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {(settings.insurers || []).map((i) => (
                    <SelectItem key={i} value={i}>{i}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="form-label">Source</Label>
              <Select
                value={filters.source || "all"}
                onValueChange={(v) => setFilters((f) => ({ ...f, source: v === "all" ? "" : v }))}
              >
                <SelectTrigger className="rounded-sm" data-testid="filter-source">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {(settings.insurance_sources || []).map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="form-label">Flagged</Label>
              <Select
                value={filters.flagged || "all"}
                onValueChange={(v) => setFilters((f) => ({ ...f, flagged: v === "all" ? "" : v }))}
              >
                <SelectTrigger className="rounded-sm" data-testid="filter-flagged">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="true">Flagged only</SelectItem>
                  <SelectItem value="false">Not flagged</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="pt-4">
            <Button variant="outline" className="rounded-sm" onClick={clearFilters} data-testid="filter-clear">
              Clear
            </Button>
            <Button className="rounded-sm bg-black text-white hover:bg-gray-800" onClick={applyFilters} data-testid="filter-apply">
              Apply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add / Edit Modal */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent
          className="rounded-sm max-w-3xl max-h-[90vh] overflow-y-auto"
          data-testid="policy-form-modal"
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
          closeClassName="p-1 transition-colors hover:bg-red-600 hover:text-white hover:opacity-100"
          onCloseClick={(e) => {
            if (JSON.stringify(formData) !== formSnapshotRef.current || policyFile) {
              e.preventDefault();
              setFormDiscardOpen(true);
            }
          }}
        >
          <DialogHeader>
            <DialogTitle className="font-heading font-bold text-xl tracking-tight uppercase">
              {editingPolicyId ? "Edit Policy" : "Add Policy"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-5 pt-2">
            {/* Vehicle */}
            <div>
              <h3 className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-3">Vehicle</h3>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="form-label">VIN *</Label>
                  <Input
                    value={formData.vin}
                    onChange={(e) => updateField("vin", e.target.value.toUpperCase().replace(/\s/g, ""))}
                    onBlur={(e) => tryPrefillFromVin(e.target.value)}
                    placeholder="Chassis number"
                    className="rounded-sm"
                    data-testid="form-vin"
                  />
                </div>
                <div>
                  <Label className="form-label">Registration No</Label>
                  <Input
                    value={formData.vehicle_reg_no}
                    onChange={(e) => updateField("vehicle_reg_no", e.target.value.toUpperCase().replace(/\s/g, ""))}
                    placeholder="WB74A1234 or PENDING"
                    className="rounded-sm"
                    data-testid="form-reg"
                  />
                </div>
                <div>
                  <Label className="form-label">Model</Label>
                  <Select
                    value={formData.model || ""}
                    onValueChange={(v) => updateField("model", v)}
                  >
                    <SelectTrigger className="rounded-sm" data-testid="form-model">
                      <SelectValue placeholder="Select model" />
                    </SelectTrigger>
                    <SelectContent>
                      {(settings.vehicle_models || []).map((m) => (
                        <SelectItem key={m} value={m}>{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Customer */}
            <div>
              <h3 className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-3">Customer</h3>
              <CustomerNameFields formData={formData} updateField={updateField} />
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div>
                  <Label className="form-label">Phone *</Label>
                  <Input
                    value={formData.customer_phone}
                    onChange={(e) => updateField("customer_phone", e.target.value)}
                    placeholder="10-digit mobile"
                    className="rounded-sm"
                    data-testid="form-phone"
                  />
                </div>
                <div>
                  <Label className="form-label">Alternate Phone</Label>
                  <Input
                    value={formData.alternate_phone}
                    onChange={(e) => updateField("alternate_phone", e.target.value)}
                    placeholder="Optional"
                    className="rounded-sm"
                    data-testid="form-alt-phone"
                  />
                </div>
              </div>
            </div>

            {/* Policy */}
            <div>
              <h3 className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-3">Policy</h3>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="form-label">Insurer *</Label>
                  <Select
                    value={formData.insurer || ""}
                    onValueChange={(v) => updateField("insurer", v)}
                  >
                    <SelectTrigger className="rounded-sm" data-testid="form-insurer">
                      <SelectValue placeholder="Select insurer" />
                    </SelectTrigger>
                    <SelectContent>
                      {(settings.insurers || []).map((i) => (
                        <SelectItem key={i} value={i}>{i}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="form-label">Policy Number *</Label>
                  <Input
                    value={formData.policy_number}
                    onChange={(e) => updateField("policy_number", e.target.value)}
                    placeholder="POL1234567"
                    className="rounded-sm"
                    data-testid="form-policy-number"
                  />
                </div>
                <div>
                  <Label className="form-label">Coverage Type *</Label>
                  <Select
                    value={formData.coverage_type}
                    onValueChange={(v) => updateField("coverage_type", v)}
                  >
                    <SelectTrigger className="rounded-sm" data-testid="form-coverage">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {COVERAGE_TYPES.map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="form-label">OD Valid From *</Label>
                  <Input
                    type="date"
                    value={formData.od_valid_from}
                    onChange={(e) => updateField("od_valid_from", e.target.value)}
                    className="rounded-sm"
                    data-testid="form-od-from"
                  />
                </div>
                <div>
                  <Label className="form-label">OD Valid To *</Label>
                  <Input
                    type="date"
                    value={formData.od_valid_to}
                    onChange={(e) => updateField("od_valid_to", e.target.value)}
                    className="rounded-sm"
                    data-testid="form-od-to"
                  />
                </div>
                <div>
                  <Label className="form-label">Policy Premium (₹)</Label>
                  <Input
                    type="number"
                    value={formData.policy_premium}
                    onChange={(e) => updateField("policy_premium", e.target.value)}
                    placeholder="0.00"
                    className="rounded-sm"
                    data-testid="form-premium"
                  />
                </div>
                <div>
                  <Label className="form-label">Source *</Label>
                  <Select
                    value={formData.source || ""}
                    onValueChange={(v) => updateField("source", v)}
                  >
                    <SelectTrigger className="rounded-sm" data-testid="form-source">
                      <SelectValue placeholder="Select source" />
                    </SelectTrigger>
                    <SelectContent>
                      {(settings.insurance_sources || []).map((s) => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {reconfirmWarning && (
              <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-sm" data-testid="reconfirm-warning">
                <AlertTriangle className="w-4 h-4 text-amber-700 mt-0.5" strokeWidth={2} />
                <p className="text-xs text-amber-800">
                  <span className="font-semibold">Heads up:</span> {reconfirmWarning}. You can still save — it will appear flagged in the list.
                </p>
              </div>
            )}

            {/* Document */}
            <div>
              <h3 className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-3">Policy Document (optional)</h3>
              <div className="flex items-center gap-3">
                <input
                  id="policy-file-input"
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f && f.size > 5 * 1024 * 1024) {
                      toast.error("Max file size is 5MB");
                      return;
                    }
                    setPolicyFile(f || null);
                  }}
                  data-testid="form-file-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  className="rounded-sm"
                  onClick={() => document.getElementById("policy-file-input")?.click()}
                  data-testid="form-file-pick"
                >
                  <Upload className="w-4 h-4 mr-2" strokeWidth={1.5} />
                  {policyFile ? "Change File" : "Select File"}
                </Button>
                {policyFile && (
                  <span className="text-xs text-gray-600 flex items-center gap-1">
                    <FileText className="w-3.5 h-3.5" strokeWidth={1.5} />
                    {policyFile.name}
                  </span>
                )}
              </div>
            </div>
          </div>

          <DialogFooter className="pt-4">
            <Button variant="outline" className="rounded-sm" onClick={() => setFormOpen(false)} data-testid="form-cancel">
              <X className="w-4 h-4 mr-1" strokeWidth={1.5} />
              Cancel
            </Button>
            <Button
              className="rounded-sm bg-black text-white hover:bg-gray-800"
              onClick={handleSave}
              disabled={saving}
              data-testid="form-save"
            >
              <Save className="w-4 h-4 mr-1" strokeWidth={1.5} />
              {saving ? "Saving..." : editingPolicyId ? "Save Changes" : "Create Policy"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Drawer */}
      <DiscardConfirm
        open={formDiscardOpen}
        onOpenChange={setFormDiscardOpen}
        onDiscard={() => {
          setFormDiscardOpen(false);
          setFormOpen(false);
        }}
      />

      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="policy-drawer">
          {drawerPolicy && (
            <>
              <SheetHeader>
                <SheetTitle className="font-heading font-bold text-xl tracking-tight uppercase flex items-center gap-2">
                  Policy {drawerPolicy.policy_number}
                  {drawerPolicy.reconfirm_flag && (
                    <Flag className="w-4 h-4 text-red-600" strokeWidth={2} />
                  )}
                </SheetTitle>
              </SheetHeader>
              <div className="mt-6 space-y-6">
                {/* Status */}
                <div className="flex items-center gap-3">
                  <StatusBadge status={drawerPolicy.status} />
                  <span className="text-xs text-gray-500">
                    Ageing: {drawerPolicy.ageing} day(s)
                  </span>
                </div>

                {/* Customer block */}
                <DrawerSection title="Customer">
                  <Row label="Name" value={drawerPolicy.customer_name} />
                  <Row label="Phone" value={drawerPolicy.customer_phone} />
                  {drawerPolicy.alternate_phone && (
                    <Row label="Alternate" value={drawerPolicy.alternate_phone} />
                  )}
                </DrawerSection>

                {/* Vehicle block */}
                <DrawerSection title="Vehicle">
                  <Row label="VIN" value={drawerPolicy.vin} />
                  <Row label="Reg No" value={drawerPolicy.vehicle_reg_no} />
                  <Row label="Model" value={drawerPolicy.model || "-"} />
                </DrawerSection>

                {/* Policy block */}
                <DrawerSection title="Policy">
                  <Row label="Insurer" value={drawerPolicy.insurer} />
                  <Row label="Coverage" value={drawerPolicy.coverage_type} />
                  <Row label="Valid From" value={formatDate(drawerPolicy.od_valid_from)} />
                  <Row label="Valid To" value={formatDate(drawerPolicy.od_valid_to)} />
                  <Row
                    label="Premium"
                    value={
                      drawerPolicy.policy_premium != null
                        ? `₹${Number(drawerPolicy.policy_premium).toLocaleString("en-IN")}`
                        : "-"
                    }
                  />
                  <Row label="Source" value={drawerPolicy.source} />
                </DrawerSection>

                {/* Document */}
                {drawerPolicy.has_policy_file && (
                  <DrawerSection title="Document">
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-sm"
                      onClick={() => downloadFile(drawerPolicy.policy_id)}
                      data-testid="drawer-download-file"
                    >
                      <Download className="w-4 h-4 mr-2" strokeWidth={1.5} />
                      Download Policy File
                    </Button>
                  </DrawerSection>
                )}

                {/* History */}
                <DrawerSection title={`History (${drawerHistory.length})`}>
                  {drawerHistory.length === 0 ? (
                    <p className="text-sm text-gray-500">No earlier policies for this VIN.</p>
                  ) : (
                    <div className="space-y-2">
                      {drawerHistory.map((h) => (
                        <div
                          key={h.policy_id}
                          className="border border-gray-200 rounded-sm p-3 text-xs space-y-1"
                          data-testid={`history-${h.policy_id}`}
                        >
                          <div className="flex items-center justify-between">
                            <span className="font-semibold">{h.policy_number}</span>
                            <StatusBadge status={h.status} />
                          </div>
                          <div className="text-gray-600">
                            {h.insurer} · {h.coverage_type}
                          </div>
                          <div className="text-gray-500">
                            {formatDate(h.od_valid_from)} → {formatDate(h.od_valid_to)}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </DrawerSection>

                {/* Footer actions */}
                <div className="flex gap-2 pt-2 border-t border-gray-100">
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-sm"
                    onClick={() => {
                      setDrawerOpen(false);
                      openEdit(drawerPolicy);
                    }}
                    data-testid="drawer-edit"
                  >
                    <Pencil className="w-3.5 h-3.5 mr-1" strokeWidth={1.5} />
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-sm text-red-600 border-red-200 hover:bg-red-50"
                    onClick={() => handleDelete(drawerPolicy.policy_id)}
                    disabled={deletingPolicy}
                    data-testid="drawer-delete"
                  >
                    <Trash2 className="w-3.5 h-3.5 mr-1" strokeWidth={1.5} />
                    Delete
                  </Button>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Column Modals */}
      <ColumnModals
        columnManager={columnManager}
        defaultColumns={DEFAULT_COLUMNS}
        customizableIds={CUSTOMIZABLE_IDS}
      />
    </div>
  );
}

function DrawerSection({ title, children }) {
  return (
    <div>
      <h3 className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-2">
        {title}
      </h3>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-3 text-sm py-1 border-b border-gray-50 last:border-b-0">
      <span className="text-gray-500">{label}</span>
      <span className="font-medium text-right">{value}</span>
    </div>
  );
}
